import { z } from 'zod';

// There is deliberately NO hardcoded list of question types here. `type` is whatever
// Dictionary.tblQuestionnaireCustomQuestionType id the backend used (see
// JurorApi.Shared.Questionnaire.QuestionTypeLookup) - opaque, just an id string, fetched live via
// GET /question-types for the builder's type picker. `inputKind` is the one piece of shape
// metadata every question needs for validation/rendering (is the answer a string? an array? does
// it need a date picker?) - computed server-side from `type` by the same QuestionTypeLookup and
// carried on every question, so this package never re-derives type behavior from a name it
// invented itself.
export const INPUT_KINDS = [
  'text',
  'email',
  'tel',
  'numeric',
  'decimal',
  'long-text',
  'date',
  'boolean',
  'single-checkbox',
  'options-single',
  'options-multi',
  'address',
  // No legacy QuestionnaireCustomQuestionType backs a file upload - this can appear only on
  // already-answered legacy data (if any), never as a type the live /question-types list offers.
  'file',
] as const;

export const inputKindSchema = z.enum(INPUT_KINDS);
export type InputKind = z.infer<typeof inputKindSchema>;

export const followUpBranchSchema = z.enum(['yes', 'no']);
export type FollowUpBranch = z.infer<typeof followUpBranchSchema>;

const OPTIONS_KINDS: ReadonlySet<InputKind> = new Set(['options-single', 'options-multi']);
const FOLLOW_UP_KINDS: ReadonlySet<InputKind> = new Set(['boolean']);

// Max lengths mirror the actual Dictionary.tblQuestionnaireCustomQuestion column sizes (verified
// against the live DB), not arbitrary limits - QuestionText is varchar(250), FieldName is
// varchar(255), ReportFieldName/DefaultValue are varchar(50). Enforcing them here surfaces a
// friendly validation error instead of a raw SQL truncation exception on save.
const baseQuestionShape = {
  id: z.string().min(1),
  // The legacy QuestionnaireCustomQuestionTypeID, as a string - not a name/slug.
  type: z.string().min(1),
  inputKind: inputKindSchema,
  text: z
    .string()
    .min(1, 'Question text is required')
    .max(250, 'Question text must be 250 characters or fewer'),
  helperText: z.string().optional(),
  required: z.boolean(),
  confidential: z.boolean(),
  options: z.array(z.string().min(1)).optional(),
  // Legacy QuestionnaireCustomQuestion columns with no dedicated UI home before now - see
  // JurorApi.Shared.Questionnaire.BuilderQuestionnaireAdapter. fieldName/reportFieldName tie the
  // question to a QuestionnairesRow column and a print/export report field; defaultValue seeds
  // new responses; minValue/maxValue bound numeric answers.
  fieldName: z.string().max(255, 'Field name must be 255 characters or fewer').optional(),
  reportFieldName: z.string().max(50, 'Report field name must be 50 characters or fewer').optional(),
  defaultValue: z.string().max(50, 'Default value must be 50 characters or fewer').optional(),
  minValue: z.number().optional(),
  maxValue: z.number().optional(),
  // Server-controlled: true only for the synthetic Name & Address question every
  // questionnaire is prepended with (see BuilderQuestionnaireAdapter.CreateLockedNameAddressQuestion).
  // Staff can't retype, reorder, or delete a locked question - the UI must always render it.
  locked: z.boolean().optional(),
};

export interface Question {
  id: string;
  type: string;
  inputKind: InputKind;
  text: string;
  helperText?: string;
  required: boolean;
  confidential: boolean;
  options?: string[];
  fieldName?: string;
  reportFieldName?: string;
  defaultValue?: string;
  minValue?: number;
  maxValue?: number;
  locked?: boolean;
  followUps?: Partial<Record<FollowUpBranch, Question[]>>;
}

export const questionSchema: z.ZodType<Question> = z.lazy(() =>
  z
    .object({
      ...baseQuestionShape,
      followUps: z
        .object({
          yes: z.array(questionSchema).optional(),
          no: z.array(questionSchema).optional(),
        })
        .partial()
        .optional(),
    })
    .superRefine((q, ctx) => {
      if (OPTIONS_KINDS.has(q.inputKind) && (!q.options || q.options.length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Options are required for this question type',
          path: ['options'],
        });
      }
      if (!FOLLOW_UP_KINDS.has(q.inputKind) && q.followUps) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Only Yes/No questions may have follow-ups',
          path: ['followUps'],
        });
      }
    }),
);

export const sectionSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1, 'Section title is required'),
  description: z.string().optional(),
  iconKey: z.string().optional(),
  questions: z.array(questionSchema),
});
export type Section = z.infer<typeof sectionSchema>;

export const questionnaireSchema = z.object({
  id: z.string().min(1),
  jurisdiction: z.string().min(1),
  term: z.string().min(1),
  // Dictionary.tblQuestionnaireCustom.Name is varchar(50) - the questionnaire's title maps
  // directly to that column (see BuilderQuestionnaireAdapter.Save).
  title: z.string().min(1).max(50, 'Title must be 50 characters or fewer'),
  intro: z.string(),
  // Dictionary.tblQuestionnaireCustom.ReportID (int) - links this questionnaire to a print/export
  // report template. Null/blank means unlinked; must be digits only when set.
  reportId: z
    .string()
    .regex(/^\d*$/, 'Report ID must be numeric')
    .optional(),
  sections: z.array(sectionSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Questionnaire = z.infer<typeof questionnaireSchema>;

export const addressAnswerSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
});
export type AddressAnswer = z.infer<typeof addressAnswerSchema>;

export const fileAnswerSchema = z.object({
  fileName: z.string(),
  storageKey: z.string().optional(),
});
export type FileAnswer = z.infer<typeof fileAnswerSchema>;

export const answerValueSchema = z.union([
  z.string(),
  z.array(z.string()),
  addressAnswerSchema,
  fileAnswerSchema,
  z.null(),
]);
export type AnswerValue = z.infer<typeof answerValueSchema>;

export const responsePayloadSchema = z.object({
  questionnaireId: z.string().min(1),
  jurorId: z.string().optional(),
  answers: z.record(z.string(), answerValueSchema),
});
export type ResponsePayload = z.infer<typeof responsePayloadSchema>;

export const presignPayloadSchema = z.object({
  questionnaireId: z.string().min(1),
  questionId: z.string().min(1),
  fileName: z.string().min(1),
  contentType: z.string().optional(),
});
export type PresignPayload = z.infer<typeof presignPayloadSchema>;

const ADDRESS_KEYS = ['street', 'city', 'state', 'zip'] as const;
const isAddressEmpty = (a: AddressAnswer | undefined) =>
  !a || ADDRESS_KEYS.every((k) => !a[k]?.trim());

const isAnswerEmpty = (inputKind: InputKind, value: unknown): boolean => {
  if (value === undefined || value === null || value === '') return true;
  if (inputKind === 'options-multi') {
    return !Array.isArray(value) || value.length === 0;
  }
  if (inputKind === 'address') {
    return isAddressEmpty(value as AddressAnswer | undefined);
  }
  if (inputKind === 'file') {
    const file = value as FileAnswer | string | undefined;
    if (typeof file === 'string') return file.trim() === '';
    return !file?.fileName;
  }
  return false;
};

const isAnswerCorrectType = (
  inputKind: InputKind,
  value: unknown,
): boolean => {
  if (value === null || value === undefined) return true;
  switch (inputKind) {
    case 'options-multi':
      return Array.isArray(value) && value.every((v) => typeof v === 'string');
    case 'address':
      return addressAnswerSchema.safeParse(value).success;
    case 'file':
      return (
        typeof value === 'string' || fileAnswerSchema.safeParse(value).success
      );
    default:
      return typeof value === 'string';
  }
};

const visibleQuestions = (
  questions: Question[],
  answers: Record<string, AnswerValue>,
): Question[] => {
  const visible: Question[] = [];
  for (const q of questions) {
    visible.push(q);
    if (!FOLLOW_UP_KINDS.has(q.inputKind) || !q.followUps) continue;
    const answer = answers[q.id];
    if (answer === 'yes' && q.followUps.yes) {
      visible.push(...visibleQuestions(q.followUps.yes, answers));
    } else if (answer === 'no' && q.followUps.no) {
      visible.push(...visibleQuestions(q.followUps.no, answers));
    }
  }
  return visible;
};

/**
 * Build a Zod schema for a juror response that enforces:
 *  - every required AND visible question has a non-empty answer
 *  - answer values match their question type
 *
 * Visibility is computed from the supplied answers, so the schema is meant to
 * be rebuilt on submit (or whenever the form is validated server-side).
 */
export const buildResponseValidator = (
  questionnaire: Questionnaire,
  answers: Record<string, AnswerValue>,
) => {
  const allQuestions = questionnaire.sections.flatMap((s) =>
    visibleQuestions(s.questions, answers),
  );

  return z
    .object({
      questionnaireId: z.literal(questionnaire.id),
      jurorId: z.string().optional(),
      answers: z.record(z.string(), answerValueSchema),
    })
    .superRefine((payload, ctx) => {
      for (const q of allQuestions) {
        const value = payload.answers[q.id];
        if (q.required && isAnswerEmpty(q.inputKind, value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'This field is required',
            path: ['answers', q.id],
          });
          continue;
        }
        if (value !== undefined && !isAnswerCorrectType(q.inputKind, value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid value for ${q.inputKind}`,
            path: ['answers', q.id],
          });
        }
      }
    });
};
